
import { useState, useEffect, useRef, useCallback } from "react";
import { flushSync } from "react-dom";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/AuthContext";
import { fetchCountryByName, fetchCategoriesByCountryId, fetchUserFilterPreferences, fetchCountryDefaultPreferences } from "@/services/countryService";
import { supabase } from "@/integrations/supabase/client";
import { CategoryDefinition, CountryData } from "@/types/filter";
import { Currency } from "@/types/banknote";
import { fetchSultanOrdersByCountryId, SultanOrder } from "@/services/sultanOrderService";

interface UseCountryDataProps {
  countryName: string;
  navigate: (path: string, options?: { replace?: boolean }) => void;
}

interface UseCountryDataResult {
  countryId: string;
  countryData: CountryData | null;
  categoryOrder: { name: string; name_ar?: string; name_tr?: string; order: number }[];
  sultans: SultanOrder[];
  currencies: Currency[];
  loading: boolean;
  groupMode: boolean;
  hasLoadedPreferences: React.MutableRefObject<boolean>;
  setGroupMode: (mode: boolean) => void;
  handleGroupModeChange: (mode: boolean) => void;
}

export const useCountryData = ({ 
  countryName, 
  navigate 
}: UseCountryDataProps): UseCountryDataResult => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [countryId, setCountryId] = useState<string>("");
  const [countryData, setCountryData] = useState<CountryData | null>(null);
  const [categoryOrder, setCategoryOrder] = useState<{ name: string; name_ar?: string; name_tr?: string; order: number }[]>([]);
  const [sultans, setSultans] = useState<SultanOrder[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [groupMode, setGroupMode] = useState<boolean>(false);
  
  // Add refs to track states and prevent render loops
  const hasLoadedPreferences = useRef<boolean>(false);
  const isGroupModeChanging = useRef<boolean>(false);
  const initialLoadComplete = useRef<boolean>(false);
  // Guards the "country not found" redirect so it fires exactly once. This page
  // is kept alive (see keepAlive.ts), so the effect re-runs whenever `user`
  // changes (auth init, token refresh, tab focus). Without this guard a stale
  // not-found instance (e.g. an SEO-indexed /catalog/Lebanon) would re-issue
  // navigate('/catalog') on every auth event, yanking the user off whatever
  // catalog they had opened.
  const redirectedNotFound = useRef<boolean>(false);

  useEffect(() => {
    const loadCountryData = async () => {
      if (!countryName) {
        console.log("CountryDetail: No country name provided");
        return;
      }

      // Already redirected this instance for a missing country — don't re-fetch
      // or re-navigate on subsequent effect runs.
      if (redirectedNotFound.current) {
        return;
      }

      try {
        // Make sure we're using the decoded country name
        const decodedCountryName = decodeURIComponent(countryName);

        const countryData = await fetchCountryByName(decodedCountryName);

        if (!countryData) {
          redirectedNotFound.current = true;
          navigate('/catalog', { replace: true });
          return;
        }

        setCountryId(countryData.id);
        setCountryData(countryData);

        const categories = await fetchCategoriesByCountryId(countryData.id);
        
        const orderMap = categories.map((cat: CategoryDefinition) => ({
          name: cat.name,
          name_ar: cat.name_ar,
          name_tr: cat.name_tr,
          order: cat.display_order
        }));
        setCategoryOrder(orderMap);

        // Fetch sultans with translation fields
        const sultans = await fetchSultanOrdersByCountryId(countryData.id);
        
        setSultans(sultans);

        const { data: currencyRows, error: currencyError } = await supabase
          .from("currencies")
          .select("id, name, display_order, country_id")
          .eq("country_id", countryData.id)
          .order("display_order", { ascending: true });

        if (currencyError) {
          console.error("Error fetching currencies:", currencyError);
          setCurrencies([]);
        } else if (currencyRows) {
          setCurrencies(currencyRows as Currency[]);
        }

        // Try to load group mode from user preferences - but only if we haven't loaded it already
        if (user?.id && !hasLoadedPreferences.current && !initialLoadComplete.current) {
          try {
            const preferences = await fetchUserFilterPreferences(user.id, countryData.id);
            if (preferences && typeof preferences.group_mode === 'boolean') {
              setGroupMode(preferences.group_mode);
              hasLoadedPreferences.current = true;
            }
          } catch (err) {
            console.error("Error loading group mode from preferences:", err);
          }
        } else if (!user && !hasLoadedPreferences.current && !initialLoadComplete.current) {
          // Anonymous: session storage first, then country-level admin default.
          try {
            const savedMode = sessionStorage.getItem(`groupMode-${countryData.id}`);
            if (savedMode !== null) {
              const parsedMode = JSON.parse(savedMode);
              setGroupMode(parsedMode);
              hasLoadedPreferences.current = true;
            } else {
              const anonDefaults = await fetchCountryDefaultPreferences(countryData.id, 'anonymous');
              if (anonDefaults && typeof anonDefaults.group_mode === 'boolean') {
                setGroupMode(anonDefaults.group_mode);
                hasLoadedPreferences.current = true;
              }
            }
          } catch (err) {
            console.error("Error loading group mode for anonymous user:", err);
          }
        }
        
        initialLoadComplete.current = true;
        setLoading(false);
      } catch (error) {
        console.error("CountryDetail: Error loading country data:", error);
        toast({
          title: "Error",
          description: "Failed to load country data. Please try again later.",
          variant: "destructive",
        });
        setLoading(false);
      }
    };

    loadCountryData();
  }, [countryName, navigate, toast, user]); // Removed onGroupModeChange from dependencies

  

  const handleGroupModeChange = useCallback((mode: boolean) => {
    
    // Update state immediately with flushSync
    flushSync(() => {
      setGroupMode(mode);
    });
    
    // Store in session storage as a fallback for non-logged in users
    if (!user) {
      try {
        sessionStorage.setItem(`groupMode-${countryId}`, JSON.stringify(mode));
      } catch (e) {
        console.error("Unable to store group mode in session storage:", e);
      }
    }
    
    // Notify any subscribers that the mode has changed
    window.dispatchEvent(new CustomEvent('groupModeChange', { detail: { mode } }));
  }, [countryId, user]);

  return {
    countryId,
    countryData,
    categoryOrder,
    sultans,
    currencies,
    loading,
    groupMode,
    hasLoadedPreferences,
    setGroupMode,
    handleGroupModeChange
  };
};
